import { selectors } from 'modules/common';
import { useSelector } from 'modules/react-redux';
import { FC, memo, useCallback, useMemo, useState } from 'react';
import { useMount } from 'react-use';
import { amplitudeService } from 'services/amplitude';

import { AdditionalScreen, FirstScreen } from './components';
import { getAdditionalScreenItems, getGLType, getPath } from './PreAhaMoment.helpers';
import { PreAhaMomentProps } from './PreAhaMoment.types';

export const PreAhaMoment: FC<PreAhaMomentProps> = memo((props) => {
    const { answerAboutGL, useCases, onNext } = props;

    const [step, setStep] = useState(1);
    const glType = useMemo(() => getGLType(answerAboutGL), [answerAboutGL]);
    const path = useMemo(() => getPath(useCases), [useCases]);
    const profile = useSelector(selectors.profile.findProfile);

    const additionalScreenItems = useMemo(() => getAdditionalScreenItems(glType, path), [glType, path]);

    useMount(() => {
        amplitudeService.sendData('sign up: land on pre-aha moment page 1', {
            gl_type: glType,
            user_id: profile?.id,
        });
    });

    const handleNext = useCallback(() => {
        if (step <= additionalScreenItems.length) {
            const nextStep = step + 1;

            setStep(nextStep);

            amplitudeService.sendData(`sign up: land on pre-aha moment page ${nextStep}`, {
                gl_type: glType,
                user_id: profile?.id,
            });

            return;
        }

        onNext?.();

        amplitudeService.sendData('sign-up: clicked Let’s explore on pre aha last screen', {
            gl_type: glType,
            user_id: profile?.id,
        });
    }, [additionalScreenItems.length, glType, onNext, profile?.id, step]);

    const handleGoToSetup = useCallback(() => {
        amplitudeService.sendData('sign up: clicked go to setup on pre-aha moment screens', {
            gl_type: glType,
            user_id: profile?.id,
            page_count: step,
        });

        onNext?.();
    }, [glType, onNext, profile?.id, step]);

    return step === 1 ? (
        <FirstScreen
            path={path}
            typeOfGL={glType}
            stepsCount={4 + additionalScreenItems.length}
            onNext={handleNext}
            onGoToSetup={handleGoToSetup}
        />
    ) : (
        <AdditionalScreen
            items={additionalScreenItems[step - 2]}
            stepsCount={4 + additionalScreenItems.length}
            currentStep={2 + step}
            isLast={step === additionalScreenItems.length + 1}
            onNext={handleNext}
            onGoToSetup={handleGoToSetup}
        />
    );
});

PreAhaMoment.displayName = 'PreAhaMoment';
